import { FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useVideoCreatorStore } from '@src/stores-v2';
import { useBRollUpload } from './BRollUploadContext';

import Modal from '../common/Modal';

type Props = {};

const EmailModal: React.FC<Props> = (props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { bRollService } = useBRollUpload();

  const [showContributorModal, setShowContributorModal] = useState(false);

  // Check if contributor information exists
  useEffect(() => {
    const contributor = localStorage.getItem('broll-contributor');
    if (!contributor || contributor === '') {
      setShowContributorModal(true);
      return;
    }

    try {
      const parsedContributor = JSON.parse(contributor);
      if (
        !parsedContributor.name ||
        !parsedContributor.email ||
        !validateEmail(parsedContributor.email)
      ) {
        setShowContributorModal(true);
      } else {
        // Set contributor info in the store
        bRollService.saveContributor({
          name: parsedContributor.name,
          email: parsedContributor.email,
        });
      }
    } catch (error) {
      localStorage.removeItem('broll-contributor');
      setShowContributorModal(true);
    }
  }, []);

  const [contributor, setContributor] = useState<
    Record<'name' | 'email', string>
  >({ name: '', email: '' });

  const validateEmail = (email: string) => {
    const regex = /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const handleSaveContributor = (
    contributor: Record<'name' | 'email', string>,
  ) => {
    if (validateEmail(contributor.email) && contributor.name) {
      localStorage.setItem('broll-contributor', JSON.stringify(contributor));
      bRollService.saveContributor({
        name: contributor.name,
        email: contributor.email,
      });
      setShowContributorModal(false);
    } else {
      videoCreator.toastState = {
        state: 'error',
        message: 'Please enter a valid name and email',
      };
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSaveContributor(contributor);
  };

  return (
    <Modal
      isOpen={showContributorModal}
      backgroundColor="#03041977"
      contentStyle={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Content>
        <Form onSubmit={handleSubmit}>
          <Heading>Enter your email</Heading>
          <FieldSet>
            <FieldName>Name</FieldName>
            <Input
              type="text"
              required
              placeholder="Please enter your name"
              value={contributor.name}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) =>
                setContributor({ ...contributor, name: e.target.value })
              }
            />
          </FieldSet>
          <FieldSet>
            <FieldName>Email</FieldName>
            <Input
              type="email"
              required
              placeholder="Please enter your email"
              value={contributor.email}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) =>
                setContributor({ ...contributor, email: e.target.value })
              }
            />
          </FieldSet>
          <SaveButton type="submit">Save</SaveButton>
        </Form>
      </Content>
    </Modal>
  );
};

const Content = styled.div`
  width: 100%;
  background-color: #030419;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border: 1px solid #484848;
  border-radius: 10px;
  padding: 32px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  @media (min-width: 460px) {
    max-width: 460px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Heading = styled.div`
  color: #45d483;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1.92px;
  text-transform: uppercase;
`;

const FieldSet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

const Input = styled.input`
  border-radius: 8px;
  border: 1px solid #484848;
  padding: 16px;
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  background: #03041a;
`;

const FieldName = styled.div`
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
`;

const SaveButton = styled.button`
  width: 100%;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: none;
  background-color: #17c964;
  color: #03041a;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background-color: #14b357;
  }
`;

export default EmailModal;
